import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { updateUserPermissions, UserWithPermissions, getAdminRoles } from '@/lib/firebase';
import { User, Mail, Shield, Loader2 } from 'lucide-react';

interface AdminRole {
  id: string;
  roleName: string;
  description?: string;
}

interface EditUserModalProps {
  user: UserWithPermissions | null;
  open: boolean;
  onClose: () => void;
  onUserUpdated: () => void;
}

export default function EditUserModal({ user, open, onClose, onUserUpdated }: EditUserModalProps) {
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [adminRoles, setAdminRoles] = useState<AdminRole[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    getAdminRoles()
      .then(roles => setAdminRoles(roles.sort((a, b) => (a.roleName ?? '').localeCompare(b.roleName ?? '')) as AdminRole[]))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setSelectedRole(user.permissions[0] || '');
    }
  }, [user]);

  const handleSave = async () => {
    if (!user) return;

    if (!selectedRole) {
      toast({ title: "No Role Selected", description: "Please select a role for the user.", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      await updateUserPermissions(user.id, [selectedRole], user.email);
      toast({ title: "User Updated Successfully", description: `${user.email} role has been updated.` });
      onUserUpdated();
      onClose();
    } catch {
      toast({ title: "Failed to Update User", description: "There was an error updating the user role. Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedRole('');
  };

  if (!user) return null;

  const getFullName = () => {
    if (user.firstName && user.secondName) return `${user.firstName} ${user.secondName}`;
    if (user.firstName) return user.firstName;
    if (user.displayName) return user.displayName;
    return user.email;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User Role</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* User Info */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-slate-900">{getFullName()}</div>
                <div className="text-sm text-slate-600 flex items-center">
                  <Mail className="w-3 h-3 mr-1" />
                  {user.email}
                </div>
              </div>
            </div>
          </div>

          {/* Role Selection */}
          <div className="space-y-3">
            <Label>Select Role</Label>
            {adminRoles.length === 0 ? (
              <p className="text-sm text-slate-500">Loading roles…</p>
            ) : (
              <div className="space-y-2">
                {adminRoles.map((role) => {
                  const isSelected = selectedRole === role.roleName;
                  return (
                    <div
                      key={role.id}
                      onClick={() => setSelectedRole(role.roleName)}
                      className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'border-blue-500' : 'border-slate-300'
                      }`}>
                        {isSelected && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                      </div>
                      <div className="flex-1">
                        <div className={`font-medium text-sm ${isSelected ? 'text-blue-900' : 'text-slate-900'}`}>
                          {role.roleName}
                        </div>
                        {role.description && (
                          <p className="text-sm text-slate-500 mt-0.5">{role.description}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-2">
            <Button onClick={handleSave} disabled={saving || !selectedRole} className="flex-1">
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating…</>
              ) : (
                <><Shield className="w-4 h-4 mr-2" />Update Role</>
              )}
            </Button>
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
